'use client';

import SvgChevronRight from '@hc-frontend/core-icons/kit/chevron-right';
import { Placement } from '@hc-frontend/core-types';
import {
  Link,
  TrackingContextsProvider,
  useEventBus,
  useIsVisible,
  useTrackingContexts,
} from '@hc-frontend/core-ui';
import type { ComponentImpressionEvent } from '@hc-frontend/features-tracking';
import { useEffect, useRef } from 'react';

import { buildInsurancePlanQuoteContext } from '../../../utils/build-insurance-plan-quote-context';
import type { PlanSummaryCardProps } from './plan-summary-card.types';

export function PlanSummaryCard({
  children,
  planSummary,
  link,
  productType,
  listPosition,
  listPage,
  listTotal,
}: PlanSummaryCardProps) {
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const planSummaryCard = useRef<HTMLDivElement | null>(null);
  const isVisible = useIsVisible(planSummaryCard);
  const impressionTracked = useRef(false);
  const planQuoteContext = buildInsurancePlanQuoteContext({
    planSummary,
    productType,
  });

  useEffect(() => {
    if (isVisible && !impressionTracked.current) {
      eventBus.publish<ComponentImpressionEvent>({
        id: 'plan-summary-card',
        topic: 'core.ui.component.impression',
        source: null as never,
        componentImpression: {
          component: 'plan-summary-card',
          action: 'display',
          category: Placement.Enum.QUOTES,
          product: productType,
          list_position: listPosition,
          list_page: listPage,
          list_total: listTotal,
        },
        contexts: [...contexts, planQuoteContext],
      });
      impressionTracked.current = true;
    }
  }, [
    isVisible,
    impressionTracked,
    eventBus,
    planSummary,
    productType,
    listPosition,
    listPage,
    listTotal,
    planQuoteContext,
    contexts,
  ]);

  return (
    <TrackingContextsProvider contexts={[planQuoteContext]}>
      <div
        className="flex flex-col rounded bg-gray-0 px-32-32 pb-12-12 pt-24-24 align-middle shadow-md"
        ref={planSummaryCard}
      >
        {children}
        <hr className="my-12-12 h-[1px] border-none bg-gray-50" />
        <Link
          id={`plan-${planSummary.id}`}
          className="hc-typography--paragraph-md mx-auto flex items-center gap-x-12-12 px-24-24 py-12-12 font-bold leading-none"
          href={link.href}
          prefetch={false}
        >
          {link.cta}
          <SvgChevronRight className="inline-flex" width={12} height={12} />
        </Link>
      </div>
    </TrackingContextsProvider>
  );
}
